import React from 'react';
import V2Layout from '../components/V2Layout';
import {
  FadeUp, Stagger, StaggerItem, SectionHead, CtaGhost, PageHero, Check, RuleLabel,
  TextLink, AgentAvatar, OSWindow, BigStat, ClosingCta,
} from '../components/V2Shared';
import { AGENTS_REVENUE } from '../data/site';

/* the pipeline, in order of money flow */
const PIPELINE = [
  { step: 'Verify', agent: 'Amanda' },
  { step: 'Scrub', agent: 'Alayna' },
  { step: 'Submit', agent: 'Ariel' },
  { step: 'Post', agent: 'Alisha' },
  { step: 'Appeal', agent: 'Sarah' },
  { step: 'Collect', agent: 'Kianna · Julia' },
];

const VIGNETTES = {
  Amanda: {
    title: 'amanda / verification queue — 6:15 am',
    lines: [
      { who: 'System', text: 'Tomorrow: 8 patients · 8 verifications required', mono: true },
      { who: 'Amanda', text: 'Pulled procedure-level breakdowns for all eight — coverage, frequencies, deductibles' },
      { who: 'Amanda', text: 'Flagged one: crown frequency limit may apply · escalated for review' },
      { who: 'System', text: '7 verified · 1 escalation · breakdowns saved to each chart', mono: true },
    ],
  },
  Alayna: {
    title: 'alayna / pre-submission scrub',
    lines: [
      { who: 'System', text: 'Claim batch: 11 claims ready for review', mono: true },
      { who: 'Alayna', text: 'Caught a missing surface designation on a composite — fixed from the chart' },
      { who: 'Alayna', text: 'This payer bundles core buildup with the crown — adjusted before denial' },
      { who: 'System', text: '11 of 11 clean · released to Ariel', mono: true },
    ],
  },
  Ariel: {
    title: 'ariel / submission — end of day',
    lines: [
      { who: 'Ariel', text: 'Generated claims from today\'s completed treatment, narratives attached' },
      { who: 'Ariel', text: 'X-rays attached where the payer historically asks for them' },
      { who: 'System', text: 'Batch submitted · acknowledgments tracked · filing deadlines logged', mono: true },
    ],
  },
  Sarah: {
    title: 'sarah / denial analysis',
    lines: [
      { who: 'System', text: 'Denial received: D4341 SRP · reason CO-50, “not medically necessary”', mono: true },
      { who: 'Sarah', text: 'Loaded the EOB, perio charting, radiographs, and this payer\'s policy in parallel' },
      { who: 'Sarah', text: '4–6mm pockets with bleeding on probing — clinical necessity is well supported' },
      { who: 'Sarah', text: 'Appeal drafted with charting attached · one tap to submit' },
    ],
  },
  Alisha: {
    title: 'alisha / eob posting',
    lines: [
      { who: 'System', text: 'Inbound: 9 ERAs + 2 paper EOBs via fax', mono: true },
      { who: 'Alisha', text: 'Matched every service line against contracted fees' },
      { who: 'Alisha', text: 'One payer paid under contract on two lines — flagged for recovery' },
      { who: 'System', text: 'Ledger posted · deposits reconciled · day balanced', mono: true },
    ],
  },
  Kianna: {
    title: 'kianna / patient billing',
    lines: [
      { who: 'Kianna', text: 'Statement sent: “Insurance covered the exam; this is your remaining portion for the filling.”' },
      { who: 'Patient', text: '“Finally a bill I can read. Can I split this?”' },
      { who: 'Kianna', text: 'Set up a two-payment plan and sent a text-to-pay link' },
      { who: 'System', text: 'First payment settled in 4 minutes', mono: true },
    ],
  },
  Julia: {
    title: 'julia / a-r work-down',
    lines: [
      { who: 'System', text: 'Aging report refreshed · oldest balances surfaced first', mono: true },
      { who: 'Julia', text: 'Worked the 90+ bucket: 6 insurance follow-ups, 4 patient outreaches' },
      { who: 'Julia', text: 'Two accounts flagged for a human conversation — context attached' },
    ],
  },
  Laura: {
    title: 'laura / payer call — hold time 47 min',
    lines: [
      { who: 'Laura', text: 'Portal showed stale data — called the payer line directly' },
      { who: 'System', text: 'IVR navigated · hold endured · representative reached', mono: true },
      { who: 'Laura', text: 'Confirmed crown coverage · documented rep name + reference number' },
      { who: 'Laura', text: 'Result handed back to Amanda · chart updated' },
    ],
  },
};

const AgentBlock = ({ agent, index }) => {
  const vig = VIGNETTES[agent.name];
  const flip = index % 2 === 1;
  return (
    <div id={agent.name.toLowerCase()} className="scroll-mt-32">
      <RuleLabel label={`${agent.tag} / ${agent.name} — ${agent.role}`} />
      <div className={`mt-10 grid items-center gap-12 lg:grid-cols-2 lg:gap-20 ${flip ? 'lg:[&>*:first-child]:order-2' : ''}`}>
        <FadeUp>
          <div className="flex items-center gap-5">
            <AgentAvatar name={agent.name} img={agent.img} size="h-16 w-16" />
            <div>
              <h2 className="v2-display text-[32px] font-semibold leading-none text-[#0a1633] md:text-[40px]">{agent.name}</h2>
              <div className="v2-mono mt-2 text-[11.5px] font-medium uppercase tracking-[0.14em] text-[#1a46e5]">{agent.role}</div>
            </div>
          </div>
          <p className="mt-6 max-w-xl text-[16px] leading-relaxed text-[#3f4d6e]">{agent.detail}</p>
          <ul className="mt-6 space-y-2.5">
            {agent.points.map((pt) => (
              <Check key={pt}>{pt}</Check>
            ))}
          </ul>
        </FadeUp>
        <FadeUp delay={0.12}>
          <div className="v2-cross relative">
            <div className="v2-canvas rounded-2xl p-7 md:p-9">
              <OSWindow title={vig.title}>
                <div className="space-y-3 bg-white p-5">
                  {vig.lines.map((l, i) => (
                    <div key={i} className="flex gap-3">
                      <span className={`v2-mono w-16 shrink-0 pt-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                        l.who === agent.name ? 'text-[#1a46e5]' : l.mono || l.who === 'System' ? 'text-[#0f9d76]' : 'text-[#7c89a8]'
                      }`}>
                        {l.who}
                      </span>
                      <p className={`text-[13.5px] leading-relaxed ${
                        l.mono || l.who === 'System' ? 'v2-mono text-[12px] text-[#7c89a8]' : 'text-[#3f4d6e]'
                      }`}>
                        {l.text}
                      </p>
                    </div>
                  ))}
                </div>
              </OSWindow>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
};

const V2RevenueCycle = () => (
  <V2Layout title="Revenue Cycle AI — Asha">
    <PageHero
      eyebrow="The AI workforce / revenue cycle"
      title={<>A billing department that <span className="text-[#1a46e5]">never lets a claim age.</span></>}
      body="Eight AI employees running the money side end to end — verification before the visit, clean claims after it, payments posted, denials appealed with evidence, balances followed up. Backed by a US-based billing expert for strategic oversight."
    >
      <CtaGhost to="/book-demo">Watch a denial get appealed</CtaGhost>
      <TextLink to="/v2/front-office">Previous: the front office team</TextLink>
    </PageHero>

    {/* pipeline */}
    <section className="px-6 pb-12">
      <div className="mx-auto max-w-7xl">
        <FadeUp>
          <div className="overflow-x-auto">
            <div className="flex min-w-[760px] items-stretch gap-0 overflow-hidden rounded-2xl border border-[#dde5f3]">
              {PIPELINE.map((p, i) => (
                <React.Fragment key={p.step}>
                  {i > 0 && (
                    <div className="flex items-center bg-[#f6f9ff] px-1 text-[#a8bcf0]" aria-hidden>
                      <svg viewBox="0 0 12 12" className="h-3 w-3"><path d="M2 1l8 5-8 5z" fill="currentColor" /></svg>
                    </div>
                  )}
                  <div className="flex-1 bg-white px-5 py-5 text-center">
                    <div className="v2-mono text-[10.5px] font-medium uppercase tracking-[0.14em] text-[#a8bcf0]">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="v2-display mt-1.5 text-[19px] font-semibold text-[#0a1633]">{p.step}</div>
                    <div className="v2-mono mt-1 text-[10.5px] uppercase tracking-wider text-[#1a46e5]">{p.agent}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>
    </section>

    {/* roster strip */}
    <section className="px-6 pb-10">
      <div className="mx-auto max-w-7xl">
        <FadeUp>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[#dde5f3] bg-[#dde5f3] sm:grid-cols-4 lg:grid-cols-8">
            {AGENTS_REVENUE.map((a) => (
              <a key={a.name} href={`#${a.name.toLowerCase()}`} className="group flex flex-col items-center gap-2.5 bg-white px-3 py-6 text-center transition-colors hover:bg-[#f6f9ff]">
                <AgentAvatar name={a.name} img={a.img} size="h-12 w-12" />
                <div>
                  <div className="text-[13.5px] font-bold text-[#0a1633] group-hover:text-[#1a46e5]">{a.name}</div>
                  <div className="mt-0.5 text-[11px] leading-tight text-[#7c89a8]">{a.role}</div>
                </div>
              </a>
            ))}
          </div>
        </FadeUp>
      </div>
    </section>

    {/* agent dossiers */}
    <section className="px-6 py-14">
      <div className="mx-auto max-w-7xl space-y-20 md:space-y-28">
        {AGENTS_REVENUE.map((a, i) => (
          <AgentBlock key={a.name} agent={a} index={i} />
        ))}
      </div>
    </section>

    {/* human + AI panel */}
    <section className="px-6 py-24 md:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="v2-panel relative overflow-hidden rounded-[2rem] px-7 py-16 md:px-14 md:py-24">
          <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
            <SectionHead
              dark
              eyebrow="human + ai, by design"
              title={<>The agents do the volume. A human owns the strategy.</>}
              body="Every back-office deployment is paired with a US-based, AI-certified billing specialist who reviews the edge cases, owns payer relationships, and meets with your team on the numbers. The AI never makes a judgment call that should be a person's."
            />
            <Stagger className="space-y-3">
              {[
                'Agents handle the repetitive volume: verifications, scrubbing, posting, follow-up',
                'Escalations route to a named human, with full context attached',
                'Strategic billing reviews with your team — trends, payers, leaks',
                'You can see every action every agent took, and why',
              ].map((t) => (
                <StaggerItem key={t}>
                  <div className="rounded-xl border border-white/12 bg-white/5 px-5 py-4 text-[15px] leading-relaxed text-white/85">
                    {t}
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <FadeUp delay={0.15}>
            <div className="mt-16 grid grid-cols-2 gap-y-10 border-t border-white/15 pt-10 md:grid-cols-4">
              <BigStat dark value="8" label="agents on the money" />
              <BigStat dark value="16+" label="data points per denial" />
              <BigStat dark value="100%" label="of claims scrubbed" />
              <BigStat dark value="1" label="human strategist" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    <ClosingCta
      title="Bring us your ugliest denial."
      body="Book a demo and watch Sarah reason through a real CO-50 — evidence pulled, appeal drafted, ready in one tap."
    />
  </V2Layout>
);

export default V2RevenueCycle;
